import React, { PropsWithChildren } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { usePermissions } from "../lib/permissions";
import styled from "styled-components";
import { useKeycloak } from "./keycloak-provider";
import { BACKOFFICE_RESOURCE, useUserAuthInfo } from "../lib/auth";

const messages = defineMessages({
  forbidden: {
    id: "app.forbiddenSection",
    defaultMessage: "You don't have permissions",
  },
});

export interface AuthorizationPermissions {
  section: string | undefined;
  show: boolean;
  create?: boolean;
  modify?: boolean;
  delete?: boolean;
  import?: boolean;
  export?: boolean;
}

export interface AuthorizationVerifierProps {
  section?: string;
  showForbiddenMessage?: boolean;
  hasPermissions?: boolean;
  witAdminRequired?: boolean;
  AIUserRequired?: boolean;
}

export interface ForbiddenSectionProps {
  showMessage?: boolean;
}

const StyledForbiddenSection = styled.div`
  margin: 20px;
  color: #b20e0e;
  font-weight: 600;
`;

export function ForbiddenSection(props: ForbiddenSectionProps) {
  if (props.showMessage === false) {
    return null;
  }
  return (
    <StyledForbiddenSection {...props}>
      <FormattedMessage {...messages.forbidden} />
    </StyledForbiddenSection>
  );
}

export function AuthorizationVerifier(props: PropsWithChildren<AuthorizationVerifierProps>) {
  const permissions = usePermissions();
  const userAuthInfo = useUserAuthInfo();

  if (!props.section || (props.witAdminRequired && !userAuthInfo?.isWitAdmin)) {
    return <ForbiddenSection showMessage={props.showForbiddenMessage} />;
  }
  if (!props.section || (props.AIUserRequired && !userAuthInfo?.isAIUser)) {
    return <ForbiddenSection showMessage={props.showForbiddenMessage} />;
  }
  const sectionPermission = permissions.find(
    sectionPermissions => sectionPermissions.section === props.section
  );
  if ((sectionPermission && sectionPermission.show) || props.hasPermissions) {
    return <>{props.children}</>;
  }
  return <ForbiddenSection showMessage={props.showForbiddenMessage} />;
}

export function showSection(permissions: any[], section?: string) {
  if (!section) {
    return false;
  }
  const sectionPermission = permissions.find(
    sectionPermissions => sectionPermissions.section === section
  );
  return Boolean(sectionPermission?.show);
}

export function withAuthorizationVerifier<P = {}>(
  Component: React.ComponentType<P>
): React.ComponentType<P & AuthorizationVerifierProps> {
  return (props: P & AuthorizationVerifierProps) => (
    <AuthorizationVerifier
      section={props.section}
      showForbiddenMessage={props.showForbiddenMessage}
      witAdminRequired={props.witAdminRequired}
      AIUserRequired={props.AIUserRequired}
      hasPermissions={props.hasPermissions}>
      <Component {...props} />
    </AuthorizationVerifier>
  );
}

export function useRoleChecker(roles: string[]): boolean {
  const keycloak = useKeycloak();
  return roles.some(role => keycloak.hasResourceRole(role, BACKOFFICE_RESOURCE));
}

export function useAuthorizedActions(section: string | undefined): AuthorizationPermissions {
  const permissions = usePermissions();
  if (!section) {
    return {
      section: undefined,
      show: false,
      create: false,
      modify: false,
      delete: false,
      import: false,
      export: false,
    };
  }
  return permissions.find(
    sectionPermissions => sectionPermissions.section === section
  ) as AuthorizationPermissions;
}
